import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/Home.css';
import { FaSearch, FaSortAmountDown, FaBars, FaTimes, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import BookCard from './BookCard';
import books from '../data/BookDB';
import { motion } from 'framer-motion';

const authors = [
  { name: 'Jane Austen', image: '' },
  { name: 'J.K. Rowling', image: 'https://media.istockphoto.com/id/1437816897/photo/business-woman-manager-or-human-resources-portrait-for-career-success-company-we-are-hiring.jpg?s=612x612&w=0&k=20&c=tyLvtzutRh22j9GqSGI33Z4HpIwv9vL_MZw_xOE19NQ=' },
  { name: 'George Orwell', image: '' },
  { name: 'Mark Twain', image: 'https://st.depositphotos.com/1011643/1248/i/450/depositphotos_12485610-stock-photo-female-white-collar-worker-in.jpg' },
];

const staticCategories = [
  'Body, Mind & Spirit', 'Business', 'Computers', 'Health & Fitness',
  'History', 'Religion', 'Science', 'Fiction', "Children's Fiction", 'Comics & Graphic Novels',
  'Crime', 'Fantasy', 'General Fiction', 'Literary', 'Romance', 'Science Fiction', 'Thrillers',
  'Young Adult Fiction', 'Non-Fiction', 'Archaeology', 'Architecture', 'Art', 'Biography',
  'Business', "Children's Nonfiction", 'Cooking', 'Crafts & Hobbies', 'Design', 'Drama',
  'Education', 'Relationships', 'Folklore & Mythology', 'Foreign Language', 'Games & Activities',
  'Gardening', 'Health & Fitness', 'House & Home', 'Humor', 'Language Arts', 'Law',
  'Literary Collections', 'Literary Criticism', 'Mathematics', 'Media Studies', 'Medical',
  'Music', 'Nature', 'Performing Arts', 'Pets', 'Philosophy', 'Photography', 'Poetry',
  'Political Science', 'Psychology', 'Reference', 'Religion', 'Science', 'Self-Help', 'Sex',
  'Social Science', 'Sports & Recreation', 'Study Aids', 'Technology', 'Transportation',
  'Travel', 'True Crime', 'Young Adult Nonfiction'
];

export default function HomePage() {
  const [sortOption, setSortOption] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const scrollRowRefs = useRef([]);

  const handleSort = (booksArray) => {
    switch (sortOption) {
      case 'priceLowHigh':
        return [...booksArray].sort((a, b) => a.price - b.price);
      case 'priceHighLow':
        return [...booksArray].sort((a, b) => b.price - a.price);
      case 'newest':
      default:
        return booksArray;
    }
  };

  // All book sections
  const allBooks = books;
  const featuredBooks = books.filter(book => book.isFeatured);
  const bestsellerBooks = books.filter(book => book.isBestseller);

  return (
    <div className="homepage-layout">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Categories</h2>
          <FaTimes className="close-icon" onClick={() => setSidebarOpen(false)} />
        </div>
        <ul className="category-list">
          {staticCategories.map((cat, idx) => (
            <li key={idx} onClick={() => setSidebarOpen(false)}>{cat}</li>
          ))}
        </ul>
      </div>

      {/* Toggle Sidebar (Mobile) */}
      <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)}>
        <FaBars /> Categories
      </button>

      {/* Main Content */}
      <div className="main-content">
        <div className="search-sort-container">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search books..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="sort-dropdown">
            <FaSortAmountDown className="sort-icon" />
            <select value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
              <option value="newest">Newest</option>
              <option value="priceLowHigh">Price: Low to High</option>
              <option value="priceHighLow">Price: High to Low</option>
            </select>
          </div>
        </div>

        {/* Featured Sections */}
        <div className="featured-sections">
          {/* Featured Books */}
          <SectionRow
            title="Featured Books"
            items={handleSort(featuredBooks)}
            renderItem={(book) => (
              <BookCard
                key={book.id}
                id={book.id}
                title={book.title}
                image={book.image}
                price={book.price}
                offer={book.offer}
                rating={book.rating}
                reviews={book.reviewsCount}
              />
            )}
            scrollRowRefs={scrollRowRefs}
            index={0}
          />

          {/* Bestsellers */}
          <SectionRow
            title="Bestsellers"
            items={handleSort(bestsellerBooks)}
            renderItem={(book) => (
              <BookCard
                key={book.id}
                id={book.id}
                title={book.title}
                image={book.image}
                price={book.price}
                offer={book.offer}
                rating={book.rating}
                reviews={book.reviewsCount}
              />
            )}
            scrollRowRefs={scrollRowRefs}
            index={1}
          />

          {/* All Books */}
          <SectionRow
            title="All Books"
            items={handleSort(allBooks)}
            renderItem={(book) => (
              <BookCard
                key={book.id}
                id={book.id}
                title={book.title}
                image={book.image}
                price={book.price}
                offer={book.offer}
                rating={book.rating}
                reviews={book.reviewsCount}
              />
            )}
            scrollRowRefs={scrollRowRefs}
            index={2}
          />

          {/* Top Authors */}
          <SectionRow
            title="Top Authors"
            items={authors}
            renderItem={(author, i) => (
              <div key={i} className="author-card">
                {author.image ? (
                  <img src={author.image} alt={author.name} className="author-img" />
                ) : (
                  <div className="author-avatar">
                    {author.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                )}
                <p className="author-name">{author.name}</p>
              </div>
            )}
            scrollRowRefs={scrollRowRefs}
            index={3}
          />
        </div>
      </div>
    </div>
  );
}

function SectionRow({ title, items, renderItem, scrollRowRefs, index }) {
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const handleScroll = useCallback(() => {
    const container = scrollRowRefs.current[index];
    if (container) {
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
  
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  }, [index, scrollRowRefs]);
  

  useEffect(() => {
    handleScroll();
  }, [handleScroll]);

  const scrollLeft = () => {
    scrollRowRefs.current[index].scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRowRefs.current[index].scrollBy({ left: 300, behavior: 'smooth' });
  };

  return (
    <div className="featured-row">
      <div className="row-header">
        <h2>{title}</h2>
        <button className="view-all-btn">
          View All <FaArrowRight />
        </button>
      </div>

      <div className="scroll-container">
        {showLeftScroll && (
          <button className="scroll-btn left" onClick={scrollLeft}>
            <FaChevronLeft />
          </button>
        )}
        <motion.div
          className="scroll-row"
          ref={(el) => (scrollRowRefs.current[index] = el)}
          whileTap={{ cursor: 'grabbing' }}
          onScroll={handleScroll}
        >
          {items.map(renderItem)}
        </motion.div>
        {showRightScroll && (
          <button className="scroll-btn right" onClick={scrollRight}>
            <FaChevronRight />
          </button>
        )}
      </div>
    </div>
  );
}

